/**
 * TutorPrescriptionScreen — « Travail entre les lives » (`study-mission-prescription?occurrence_id=
 * &liveSessionId=`), hors live seulement : dans la pile du live, l'ancien écran reste. Un fil : les
 * élèves du live, les lectures, les gestes, le view-model, la page, et ses surfaces — l'aperçu d'une
 * ligne, le formulaire de « Recommencer », la feuille du programme et le bandeau d'un refus.
 */
import React from "react"
import { useSelector } from "react-redux"

import { ActivityDetailsModal, Snackbar } from "../../../../../../design-system"
import {
    TutorGenerateActivityFlow,
    TutorPrescriptionPage,
    TutorRevisionProgramSheet,
} from "../../../../../../design-system/tutor"
import {
    tutorPrescriptionPreviewViewModel,
    tutorPrescriptionRosterViewModel,
    tutorPrescriptionViewModel,
} from "../../../../view-models/tutor/Lives/tutorPrescriptionViewModel"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { useTutorPrescriptionData } from "./useTutorPrescriptionData"
import { useTutorPrescriptionState } from "./useTutorPrescriptionState"

export function TutorPrescriptionScreen({ route }: TutorSpaceScreenProps) {
    const occurrenceId = route?.params?.occurrence_id ?? null
    const liveSessionId = route?.params?.liveSessionId ?? null
    const routeStudents = route?.params?.students
    const { roster, waiting } = useSelector((s: unknown) =>
        tutorPrescriptionRosterViewModel(s, { routeStudents, liveSessionId })
    )
    const data = useTutorPrescriptionData(occurrenceId, roster)
    const state = useTutorPrescriptionState({ occurrenceId, liveSessionId })
    const vm = useSelector((s: unknown) =>
        tutorPrescriptionViewModel(s, { occurrenceId, roster, connected: data.connected, ui: state.ui })
    )
    const preview = useSelector((s: unknown) =>
        tutorPrescriptionPreviewViewModel(s, { occurrenceId, preview: state.preview })
    )
    const failure = state.failure ?? data.todosFailure

    return (
        <>
            <TutorPrescriptionPage
                students={vm.students}
                loading={waiting}
                error={data.error}
                onRetry={data.refetch}
                {...state.gestures}
                testID="tutor-prescription"
            />
            <ActivityDetailsModal
                visible={preview.activity != null}
                onClose={state.closePreview}
                activity={preview.activity}
                modes={preview.modes}
                mode={state.previewMode ?? undefined}
                onModeChange={state.onPreviewModeChange}
            />
            <TutorGenerateActivityFlow {...state.regenerateFlow} />
            <TutorRevisionProgramSheet {...state.programSheet} />
            <Snackbar
                visible={failure != null}
                message={failure ?? ""}
                tone="danger"
                onDismiss={state.failure != null ? state.dismissFailure : data.dismissTodosFailure}
            />
        </>
    )
}
